import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { IconButton, RoundedButton } from '../../../shared'
import { useOnScreen } from '../../../../hooks/utils'

import { QuestionnaireInstanceProps } from './QuestionnaireInstance.model'
import { QuestionList, QuestionnaireMode } from '../../../questionnaire'
import { QuestionnairePlaceholder } from './QuestionnairePlaceholder'

import styles from './QuestionnaireInstance.module.scss'
import { SelectedContentType } from '../../../../store/ui/medicalEvents/medicalEventContent'
import { QuestionInstance } from '../../../../model/Questionnaire'
import { scrollToTopSmoothly } from '../../../../misc/window.utilities'
import { isDefined } from '../../../../misc/functions.utilities'
import { EditQuestionInstanceBottomPanel } from '../../../shared/bottomPanel/EditQuestionInstanceBottomPanel'
import { useToggleImportantInformation } from '../../../../hooks/queries/importantInformations'

export const QuestionnaireInstance: FunctionComponent<QuestionnaireInstanceProps> = ({
  documentInstanceId,
  isVariable,
  questions,
  isReadonly = false,
  nextQuestionnaireId,
  disabled,
  refreshDocument,
  selectMedicalEventContent,
  saveAnswerValue,
  fetchDocumentInstance,
  fetchDocumentAnswerValues,
}) => {
  const [editQuestionId, setEditQuestionId] = useState<number | null>(null)
  const topRef = useRef<HTMLDivElement>(null)
  const { mutate: changeQuestionImportance } = useToggleImportantInformation()

  // Rechargement des questions et des réponses à chaque accès au questionnaire
  useEffect(() => {
    if (documentInstanceId) {
      fetchDocumentInstance(documentInstanceId)
      fetchDocumentAnswerValues(documentInstanceId)
    }
  }, [documentInstanceId, fetchDocumentInstance, fetchDocumentAnswerValues])

  const topOutOfScreen = !useOnScreen(topRef)

  const loadNextQuestionnaire = useCallback(
    (nextQuestionnaireId: number) => () => {
      scrollToTopSmoothly()
      selectMedicalEventContent({
        type: SelectedContentType.QUESTIONNAIRE,
        questionnaireId: nextQuestionnaireId,
      })
    },
    [selectMedicalEventContent],
  )

  const isEditable = !!documentInstanceId && isVariable && !disabled

  return (
    <div className={styles.container}>
      <div ref={topRef} />
      {questions.length === 0 ? (
        <QuestionnairePlaceholder />
      ) : (
        <>
          <QuestionList
            onEdit={
              isEditable
                ? (question: QuestionInstance) => {
                    setEditQuestionId(question.id)
                  }
                : undefined
            }
            mode={QuestionnaireMode.fill}
            questions={questions}
            onChangeQuestionImportance={(question: QuestionInstance, toggle: boolean) => {
              if (question.questionTemplateId) {
                changeQuestionImportance(
                  {
                    questionId: question.questionTemplateId,
                    toggle,
                  },
                  {
                    onSuccess: refreshDocument,
                  },
                )
              }
            }}
            setAnswerValue={(questionId, answerValue) => {
              saveAnswerValue(questionId, answerValue)
            }}
            setSubQuestionsAnswerValues={(questionId, answerValue) => {
              saveAnswerValue(questionId, answerValue)
            }}
            isReadonly={isReadonly}
            disabled={disabled}
          />
        </>
      )}
      <div className={styles.bottomActions}>
        <div className={styles.nextButton}>
          {nextQuestionnaireId ? (
            <RoundedButton
              theme="primary"
              onClick={loadNextQuestionnaire(nextQuestionnaireId)}
              icon="arrow"
              iconRotate={90}
              iconPosition="right"
              label="Questionnaire suivant"
              testId="button-next_questionnaire-load"
            />
          ) : (
            <RoundedButton
              theme="primary"
              onClick={() => selectMedicalEventContent({ type: SelectedContentType.EDITOR })}
              label="Aperçu du document"
              testId="button-document-preview-display"
            />
          )}
        </div>
        {topOutOfScreen && (
          <div className={styles.scrollTopButton}>
            <IconButton
              onClick={scrollToTopSmoothly}
              icon="arrow"
              theme="primary"
              title="Remonter"
              testId="button-questionnaire_instance-scrolltop"
            />
          </div>
        )}
      </div>
      <EditQuestionInstanceBottomPanel
        display={isDefined(editQuestionId)}
        onRequestClose={() => setEditQuestionId(null)}
        itemId={editQuestionId}
        onItemEdited={refreshDocument}
      />
    </div>
  )
}
